import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Sidebar from '../layouts/Sidebar';
import Topbar from '../layouts/Topbar';
import './UpdateEmployee.css';
import packageJson from '../../../../package.json';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Select from 'react-select';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaFileAlt, FaUpload, FaSpinner, FaExclamationTriangle, FaTimes } from 'react-icons/fa';

const UpdateEmployee = () => {
    const { id: employeeId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const baseUrl = 'https://api.burrayhr.com';

    // Modal data states
    const [modalData, setModalData] = useState({
        children: [],
        qualifications: [],
        documents: [],
        contract: {},
        allowances: [],
        contracts: []
    });

    const fetchModalData = async (type) => {
        const token = localStorage.getItem('token');
        try {
            setLoading(true);
            const response = await axios.get(`${baseUrl}/api/${type}/${employeeId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            let formattedData;
            switch(type) {
                case 'children':
                    formattedData = response.data.map(child => ({
                        id: child.id || '',
                        name: child.name || '',
                        age: child.age || '',
                        gender: child.gender || ''
                    }));
                    break;
                case 'qualifications':
                    formattedData = response.data.map(qual => ({
                        id: qual.id || '',
                        name: qual.name || '',
                        institution: qual.institution || '',
                        completion_date: qual.completion_date || ''
                    }));
                    break;
                case 'documents':
                    formattedData = response.data.map(doc => ({
                        id: doc.id || '',
                        document_type: doc.document_type || '',
                        document_file: null,
                        file_url: doc.file_url || ''
                    }));
                    break;
                case 'contract':
                    formattedData = {
                        type: response.data.type || '',
                        start_date: response.data.start_date || null,
                        end_date: response.data.end_date || null,
                        salary: response.data.salary || 0
                    };
                    break;
                case 'allowances':
                    formattedData = response.data.map(allowance => ({
                        id: allowance.id || '',
                        allowance_type: allowance.allowance_type || '',
                        amount: allowance.amount || 0
                    }));
                    break;
                case 'contracts':
                    formattedData = response.data.map(contract => ({
                        id: contract.id || '',
                        start_date: contract.start_date || '',
                        end_date: contract.end_date || '',
                        status: contract.status || ''
                    }));
                    break;
                default:
                    formattedData = [];
            }

            setModalData(prev => ({
                ...prev,
                [type]: formattedData
            }));
        } catch (error) {
            console.error(`Error fetching ${type}:`, error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Failed to fetch ${type} data: ${error.response?.data?.message || error.message}`,
                confirmButtonColor: '#d33'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleModalSubmit = async (type, data) => {
        const token = localStorage.getItem('token');
        try {
            setSubmitting(true);
            let formData;

            switch(type) {
                case 'documents':
                    // Handle file uploads
                    formData = new FormData();
                    data.forEach((doc, index) => {
                        if (doc.document_file) {
                            formData.append('document_files', doc.document_file);
                            formData.append('document_types', doc.document_type);
                        }
                    });
                    await axios.patch(`${baseUrl}/api/${type}/${employeeId}/`, formData, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    break;

                case 'children':
                    // Validate children data
                    const validChildren = data.filter(child => 
                        child.name && child.name.trim() !== '' && 
                        child.age && !isNaN(child.age) && 
                        child.gender && child.gender.trim() !== ''
                    );
                    await axios.patch(`${baseUrl}/api/${type}/${employeeId}/`, { children: validChildren }, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    break;

                case 'qualifications':
                    // Validate qualifications
                    const validQualifications = data.filter(qual => 
                        qual.name && qual.name.trim() !== ''
                    );
                    await axios.patch(`${baseUrl}/api/${type}/${employeeId}`, { qualifications: validQualifications }, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    break;

                case 'contract':
                    // Validate contract data
                    if (!data.type || !data.start_date) {
                        throw new Error('Contract type and start date are required');
                    }
                    if (data.type === 'fixed_term' && !data.end_date) {
                        throw new Error('End date is required for fixed-term contracts');
                    }
                    await axios.patch(`${baseUrl}/api/${type}/${employeeId}`, data, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    break;

                case 'allowances':
                    // Validate allowances
                    const validAllowances = data.filter(allowance => 
                        allowance.allowance_type && allowance.allowance_type.trim() !== '' && 
                        allowance.amount && !isNaN(allowance.amount) && allowance.amount > 0
                    );
                    await axios.patch(`${baseUrl}/api/${type}/${employeeId}`, { allowances: validAllowances }, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    break;
            }

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: `${type.charAt(0).toUpperCase() + type.slice(1)} updated successfully`,
                confirmButtonColor: '#3085d6'
            });
            // Refresh the data
            await fetchModalData(type);
        } catch (error) {
            console.error(`Error updating ${type}:`, error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Failed to update ${type}: ${error.response?.data?.message || error.message}`,
                confirmButtonColor: '#d33'
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Add validation functions for each type
    const validateChildren = (children) => {
        return children.every(child => 
            child.name && child.name.trim() !== '' && 
            child.age && !isNaN(child.age) && 
            child.gender && child.gender.trim() !== ''
        );
    };

    const validateQualifications = (qualifications) => {
        return qualifications.every(qual => 
            qual.name && qual.name.trim() !== ''
        );
    };

    const validateContract = (contract) => {
        if (!contract.type || !contract.start_date) {
            return false;
        }
        if (contract.type === 'fixed_term' && !contract.end_date) {
            return false;
        }
        return true;
    };

    const validateAllowances = (allowances) => {
        return allowances.every(allowance => 
            allowance.allowance_type && allowance.allowance_type.trim() !== '' && 
            allowance.amount && !isNaN(allowance.amount) && allowance.amount > 0
        );
    };

    // Add handlers for file changes
    const handleFileChange = (index, file) => {
        const newDocuments = [...modalData.documents];
        newDocuments[index] = {
            ...newDocuments[index],
            document_file: file
        };
        setModalData(prev => ({
            ...prev,
            documents: newDocuments
        }));
    };

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        position: '',
        department: '',
        workingDaysPerMonth: 22,
        dateOfBirth: '',
        location: '',
        gender: '',
        marital_status: '',
        photo: null,
        spouse_name: '',
        spouse_contact: '',
        home_address: '',
        salary: 0,
        next_of_kin_name: '',
        next_of_kin_contact: '',
        next_of_kin_address: '',
        next_of_kin_relationship: '',
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            Swal.fire({
                icon: 'error',
                title: 'Authentication Error',
                text: 'Please login to continue',
                confirmButtonColor: '#d33'
            });
            navigate('/login');
            return;
        }
        if (!employeeId) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Employee ID is required',
                confirmButtonColor: '#d33'
            });
            navigate('/admin-manager/employees');
            return;
        }
        fetchEmployeeData();
        fetchDepartments();
    }, [employeeId]);

    const fetchEmployeeData = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${baseUrl}/api/employees/${employeeId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const employee = response.data;
            console.log(employee);
            setFormData({
                firstName: employee.first_name,
                lastName: employee.last_name,
                phoneNumber: employee.phone_number,
                position: employee.position,
                department: employee.department,
                workingDaysPerMonth: employee.working_days_per_month || 22,
                location: employee.location || '',
                date_of_birth: formatDateForInput(employee.date_of_birth),
                gender: employee.gender,
                marital_status: employee.marital_status || '',
                photo: employee.photo || null,
                spouse_name: employee.spouse_name || '',
                spouse_contact: employee.spouse_contact || '',
                home_address: employee.home_address || '',
                next_of_kin_name: employee.next_of_kin_name || '',
                next_of_kin_contact: employee.next_of_kin_contact || '',
                next_of_kin_address: employee.next_of_kin_address || '',
                next_of_kin_relationship: employee.next_of_kin_relationship || '',
                salary: employee.salary || 0
            });
            setLoading(false);
        } catch (error) {
            if (error.response?.status === 401) {
                Swal.fire({
                    icon: 'error',
                    title: 'Session Expired',
                    text: 'Please login again',
                    confirmButtonColor: '#d33'
                });
                navigate('/login');
                return;
            }
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch employee data',
                confirmButtonColor: '#d33'
            });
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${baseUrl}/api/departments`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setDepartments(response.data);
            
            const employeeData = await axios.get(`${baseUrl}/api/employees/${employeeId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (employeeData.data.department) {
                const departmentObj = response.data.find(dept => dept.id === employeeData.data.department);
                if (departmentObj) {
                    setSelectedDepartment({
                        value: departmentObj.id,
                        label: departmentObj.name
                    });
                }
            }
        } catch (error) {
            if (error.response?.status === 401) {
                Swal.fire({
                    icon: 'error',
                    title: 'Session Expired',
                    text: 'Please login again',
                    confirmButtonColor: '#d33'
                });
                navigate('/login');
                return;
            }
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch departments',
                confirmButtonColor: '#d33'
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill in all required fields correctly',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            Swal.fire({
                icon: 'error',
                title: 'Authentication Error',
                text: 'Please login to continue',
                confirmButtonColor: '#d33'
            });
            navigate('/login');
            return;
        }

        setSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('first_name', formData.firstName);
            formDataToSend.append('last_name', formData.lastName);
            formDataToSend.append('phone_number', formData.phoneNumber);
            formDataToSend.append('position', formData.position);
            formDataToSend.append('department', formData.department);
            formDataToSend.append('working_days_per_month', formData.workingDaysPerMonth);
            formDataToSend.append('location', formData.location || '');
            formDataToSend.append('date_of_birth', parseDateFromInput(formData.date_of_birth));
            formDataToSend.append('gender', formData.gender);
            formDataToSend.append('marital_status', formData.marital_status);
            if (formData.photo instanceof File) {
                formDataToSend.append('photo', formData.photo);
            }
            formDataToSend.append('spouse_name', formData.marital_status === 'married' ? formData.spouse_name : '');
            formDataToSend.append('spouse_contact', formData.marital_status === 'married' ? formData.spouse_contact : '');
            formDataToSend.append('home_address', formData.home_address || '');
            formDataToSend.append('next_of_kin_name', formData.next_of_kin_name || '');
            formDataToSend.append('next_of_kin_contact', formData.next_of_kin_contact || '');
            formDataToSend.append('next_of_kin_address', formData.next_of_kin_address || '');
            formDataToSend.append('next_of_kin_relationship', formData.next_of_kin_relationship || '');
            formDataToSend.append('salary', formData.salary || 0);

            const response = await axios.patch(`${baseUrl}/api/employees/${employeeId}/`, formDataToSend, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Employee updated successfully',
                confirmButtonColor: '#3085d6'
            }).then(() => {
                navigate('/admin-manager/employees');
            });
        } catch (error) {
            console.error('Error updating employee:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to update employee',
                confirmButtonColor: '#d33'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        // Personal Information
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
        if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.marital_status) newErrors.marital_status = 'Marital status is required';
        
        
        // Work Information
        if (!formData.position) newErrors.position = 'Position is required';
        if (!formData.department) newErrors.department = 'Department is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleButtonClick = async (type) => {
        try {
            setLoading(true);
            await fetchModalData(type);
            
            switch(type) {
                case 'qualifications':
                    showQualificationsModal();
                    break;
                case 'documents':
                    showDocumentsModal();
                    break;
                case 'allowances':
                    showAllowancesModal();
                    break;
                case 'contracts':
                    showContractsModal();
                    break;
            }
        } catch (error) {
            console.error(`Error handling ${type} button click:`, error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Failed to load ${type} data: ${error.response?.data?.message || error.message}`,
                confirmButtonColor: '#d33'
            });
        } finally {
            setLoading(false);
        }
    };

    const showQualificationsModal = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/qualifications/${employeeId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            window.currentQualifications = response.data;

            const tableHtml = `
                <div class="table-responsive" style="max-height: 400px; overflow-y: auto; padding: 10px; border-radius: 10px;">
                    <table class="table table-bordered table-hover">
                        <thead class="bg-light">
                            <tr>
                                <th>Title</th>
                                <th class="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${response.data.map((qual) => `
                                <tr>
                                    <td>${qual.title || ''}</td>
                                    <td class="text-center d-flex justify-content-center gap-2">
                                        <button 
                                            type="button"
                                            class="btn btn-primary btn-sm"
                                            onclick="window.handleEditQualification(${qual.id})"
                                            title="Edit Qualification">
                                            Edit
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-danger btn-sm"
                                            onclick="window.handleDeleteQualification(${qual.id})"
                                            title="Delete Qualification">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;

            const { value: formValues } = await Swal.fire({
                title: 'Manage Qualifications',
                html: tableHtml,
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Add New Qualification',
                cancelButtonText: 'Close',
                width: '900px',
                didOpen: () => {
                    window.handleEditQualification = async (qualId) => {
                        try {
                            const qual = window.currentQualifications.find(q => q.id === qualId);
                            
                            if (!qual) {
                                throw new Error('Qualification not found');
                            }

                            const { value: updatedData } = await Swal.fire({
                                title: 'Edit Qualification',
                                html: `
                                    <div class="form-group mb-3" style="width: 100%; padding: 10px;">
                                        <label class="form-label">Title</label>
                                        <input
                                            type="text"
                                            id="title"
                                            class="form-control"
                                            value="${qual.title || ''}"
                                            placeholder="Enter qualification title"
                                        />
                                    </div>
                                    
                                `,
                                focusConfirm: false,
                                showCancelButton: true,
                                confirmButtonText: 'Update',
                                cancelButtonText: 'Cancel',
                                preConfirm: () => {
                                    const title = document.getElementById('title').value;
                                    

                                    if (!title) {
                                        Swal.showValidationMessage('Please fill in all fields');
                                        return false;
                                    }

                                    return { title };
                                }
                            });

                            if (updatedData) {
                                const success = await handleQualificationUpdate(qualId, updatedData);
                                if (success) {
                                    Swal.close();
                                    showQualificationsModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error editing qualification:', error);
                            await showErrorMessage(error);
                        }
                    };

                    window.handleDeleteQualification = async (qualId) => {
                        try {
                            const result = await showDeleteConfirmation();
                            if (result.isConfirmed) {
                                const success = await handleQualificationDelete(qualId);
                                if (success) {
                                    Swal.close();
                                    showQualificationsModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error deleting qualification:', error);
                            await showErrorMessage(error);
                        }
                    };
                }
            });

            if (formValues) {
                const { value: newQualData } = await Swal.fire({
                    title: 'Add New Qualification',
                    html: `
                        <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                            <label class="form-label">Title</label>
                            <input
                                type="text"
                                id="title"
                                class="form-control"
                                placeholder="Enter qualification title"
                            />
                        </div>
                        
                    `,
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: 'Add',
                    cancelButtonText: 'Cancel',
                    preConfirm: () => {
                        const title = document.getElementById('title').value;

                        if (!title) {
                            Swal.showValidationMessage('Please fill in all fields');
                            return false;
                        }

                        return { title };
                    }
                });

                if (newQualData) {
                    try {
                        const token = localStorage.getItem('token');
                        await axios.post(`${baseUrl}/api/qualifications/${employeeId}/`, newQualData, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                        await showSuccessMessage('Qualification added successfully');
                        Swal.close();
                        showQualificationsModal();
                    } catch (error) {
                        console.error('Error adding qualification:', error);
                        await showErrorMessage(error);
                    }
                }
            }
        } catch (error) {
            console.error('Error managing qualifications:', error);
            await showErrorMessage(error);
        }
    };

    const showDocumentsModal = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/documents/${employeeId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            window.currentDocuments = response.data;

            const tableHtml = `
                <div class="table-responsive" style="max-height: 400px; overflow-y: auto; padding: 10px; border-radius: 10px;">
                    <table class="table table-bordered table-hover">
                        <thead class="bg-light">
                            <tr>
                                <th>Type</th>
                                <th>File</th>
                                <th class="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${response.data.map((doc) => `
                                <tr>
                                    <td>${doc.document_type ? doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''}</td>
                                    <td>
                                        ${<FaFileAlt /> ? 
                                            `<a style={{color: 'black'}} href="${doc.document_file}" target="_blank" class="btn btn-link btn-sm">
                                                View File
                                            </a>` : 
                                            'No file'
                                        }
                                    </td>
                                    <td class="text-center d-flex justify-content-center gap-2">
                                        <button 
                                            type="button"
                                            class="btn btn-primary btn-sm"
                                            onclick="window.handleEditDocument(${doc.id})"
                                            title="Edit Document">
                                            Edit
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-danger btn-sm"
                                            onclick="window.handleDeleteDocument(${doc.id})"
                                            title="Delete Document">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;

            const { value: formValues } = await Swal.fire({
                title: 'Manage Documents',
                html: tableHtml,
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Add New Document',
                cancelButtonText: 'Close',
                width: '900px',
                didOpen: () => {
                    window.handleEditDocument = async (docId) => {
                        try {
                            const doc = window.currentDocuments.find(d => d.id === docId);
                            
                            if (!doc) {
                                throw new Error('Document not found');
                            }

                            const { value: updatedData } = await Swal.fire({
                                title: 'Edit Document',
                                html: `
                                    <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                        
                                        <label class="form-label">New Document Type</label>
                                        <select id="edit_document_type" class="form-control">
                                            <option value="">Select New Document Type</option>
                                            <option value="national_id" ${doc.document_type === 'national_id' ? 'selected' : ''}>National ID</option>
                                            <option value="passport" ${doc.document_type === 'passport' ? 'selected' : ''}>Passport</option>
                                            <option value="drivers_license" ${doc.document_type === 'drivers_license' ? 'selected' : ''}>Driver's License</option>
                                            <option value="academic_certificate" ${doc.document_type === 'academic_certificate' ? 'selected' : ''}>Academic Certificate</option>
                                            <option value="professional_certificate" ${doc.document_type === 'professional_certificate' ? 'selected' : ''}>Professional Certificate</option>
                                        </select>
                                    </div>
                                    <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                        <label class="form-label">Current File</label>
                                        ${doc.document_file ? 
                                            `<div class="alert alert-info" style="margin-bottom: 15px;">
                                                <a href="${doc.document_file}" target="_blank" class="btn btn-link btn-sm p-0">
                                                    <i class="fa fa-file-alt"></i> View Current Document
                                                </a>
                                            </div>` : 
                                            '<div class="alert alert-warning" style="margin-bottom: 15px;">No file uploaded</div>'
                                        }
                                        <label class="form-label">Upload New File (Optional)</label>
                                        <input
                                            type="file"
                                            id="edit_document_file"
                                            class="form-control"
                                            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                                            value = ${doc.document}
                                        />
                                        <small class="form-text text-muted">Leave empty to keep the current file</small>
                                    </div>
                                `,
                                focusConfirm: false,
                                showCancelButton: true,
                                confirmButtonText: 'Update',
                                cancelButtonText: 'Cancel',
                                width: '600px',
                                preConfirm: () => {
                                    const document_type = document.getElementById('edit_document_type').value;
                                    const document_file = document.getElementById('edit_document_file').files[0];

                                    if (!document_type) {
                                        Swal.showValidationMessage('Please select a document type');
                                        return false;
                                    }

                                    // Validate file size if a new file is selected (max 5MB)
                                    if (document_file) {
                                        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                                        if (document_file.size > maxSize) {
                                            Swal.showValidationMessage('File size must be less than 5MB');
                                            return false;
                                        }
                                    }

                                    const formData = new FormData();
                                    formData.append('document_type', document_type);
                                    if (document_file) {
                                        formData.append('document_file', document_file);
                                    }

                                    return formData;
                                }
                            });

                            if (updatedData) {
                                const success = await handleDocumentUpdate(docId, updatedData);
                                if (success) {
                                    Swal.close();
                                    showDocumentsModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error editing document:', error);
                            await showErrorMessage(error);
                        }
                    };

                    window.handleDeleteDocument = async (docId) => {
                        try {
                            const result = await showDeleteConfirmation();
                            if (result.isConfirmed) {
                                const success = await handleDocumentDelete(docId);
                                if (success) {
                                    Swal.close();
                                    showDocumentsModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error deleting document:', error);
                            await showErrorMessage(error);
                        }
                    };
                }
            });

            if (formValues) {
                const { value: newDocData } = await Swal.fire({
                    title: 'Add New Document',
                    html: `
                        <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                            <label class="form-label">Document Type</label>
                            <select id="new_document_type" class="form-control" onchange="handleDocumentTypeChange(this.value)">
                                <option value="">Select Document Type</option>
                                <option value="National ID">National ID</option>
                                <option value="Passport">Passport</option>
                                <option value="Driver's License">Driver's License</option>
                                <option value="Academic Certificate">Academic Certificate</option>
                                <option value="Professional Certificate">Professional Certificate</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div id="other_document_title" class="form-group mb-3" style="width: 100%; padding: 15px; display: none;">
                            <label class="form-label">Document Title</label>
                            <input
                                type="text"
                                id="document_title"
                                class="form-control"
                                placeholder="Enter document title"
                            />
                        </div>
                        <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                            <label class="form-label">Document File</label>
                            <input
                                type="file"
                                id="new_document_file"
                                class="form-control"
                                accept=".pdf,.doc,.docx,.txt"
                                required
                            />
                        </div>
                    `,
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: 'Add',
                    cancelButtonText: 'Cancel',
                    didOpen: () => {
                        // Add the function to handle document type change
                        window.handleDocumentTypeChange = (value) => {
                            const otherTitleDiv = document.getElementById('other_document_title');
                            if (value === 'Other') {
                                otherTitleDiv.style.display = 'block';
                            } else {
                                otherTitleDiv.style.display = 'none';
                            }
                        };
                    },
                    willClose: () => {
                        // Clean up the global function
                        delete window.handleDocumentTypeChange;
                    },
                    preConfirm: () => {
                        const document_type = document.getElementById('new_document_type').value;
                        const document_file = document.getElementById('new_document_file').files[0];
                        const document_title = document.getElementById('document_title').value;

                        if (!document_type || !document_file) {
                            Swal.showValidationMessage('Please fill in all required fields');
                            return false;
                        }

                        if (document_type === 'Other' && !document_title.trim()) {
                            Swal.showValidationMessage('Please enter a document title');
                            return false;
                        }

                        const formData = new FormData();
                        // If document type is "Other", use the custom title
                        formData.append('document_type', document_type === 'Other' ? document_title.trim() : document_type);
                        formData.append('document_file', document_file);
                        
                        return {
                            formData,
                            fileInfo: {
                                name: document_file.name,
                                type: document_file.type,
                                size: document_file.size
                            }
                        };
                    }
                });

                if (newDocData) {
                    try {
                        // Validate file size (e.g., max 5MB)
                        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                        if (newDocData.fileInfo.size > maxSize) {
                            await Swal.fire({
                                icon: 'error',
                                title: 'File Too Large',
                                text: 'Maximum file size is 5MB'
                            });
                            return;
                        }

                        const success = await handleDocumentAdd(newDocData.formData);
                        if (success) {
                            Swal.close();
                            showDocumentsModal();
                        }
                    } catch (error) {
                        console.error('Error adding document:', error);
                        await showErrorMessage(error);
                    }
                }
            }
        } catch (error) {
            console.error('Error managing documents:', error);
            await showErrorMessage(error);
        }
    };

    const handleDocumentAdd = async (formData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${baseUrl}/api/documents/${employeeId}/`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'  // Important for file upload
                    }
                }
            );

            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Document added successfully'
            });
            return true;
        } catch (error) {
            console.error('Error adding document:', error);
            await showErrorMessage(error);
            return false;
        }
    };

    const showAllowancesModal = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/allowances/${employeeId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            window.currentAllowances = response.data;

            const tableHtml = `
                <div class="table-responsive" style="max-height: 400px; overflow-y: auto; padding: 10px; border-radius: 10px;">
                    <table class="table table-bordered table-hover">
                        <thead class="bg-light">
                            <tr>
                                <th>Type</th>
                                <th>Title</th>
                                <th>Amount</th>
                                <th class="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${response.data.map((allowance) => `
                                <tr>
                                    <td>${(allowance.allowance_type || '').charAt(0).toUpperCase() + (allowance.allowance_type || '').slice(1)}</td>
                                    <td>${allowance.title || ''}</td>
                                    <td>${allowance.amount || 0}</td>
                                    <td class="text-center d-flex justify-content-center gap-2">
                                        <button 
                                            type="button"
                                            class="btn btn-primary btn-sm"
                                            onclick="window.handleEditAllowance(${allowance.id})"
                                            title="Edit Allowance">
                                            Edit
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-danger btn-sm"
                                            onclick="window.handleDeleteAllowance(${allowance.id})"
                                            title="Delete Allowance">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;

            const { value: formValues } = await Swal.fire({
                title: 'Manage Allowances',
                html: tableHtml,
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Add New Allowance',
                cancelButtonText: 'Close',
                width: '900px',
                didOpen: () => {
                    window.handleEditAllowance = async (allowanceId) => {
                        try {
                            const allowance = window.currentAllowances.find(a => a.id === allowanceId);
                            
                            if (!allowance) {
                                throw new Error('Allowance not found');
                            }

                            const { value: updatedData } = await Swal.fire({
                                title: 'Edit Allowance',
                                html: `
                                    <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                        <label class="form-label">Allowance Type</label>
                                        <select id="edit_allowance_type" class="form-control" onchange="handleEditAllowanceTypeChange()">
                                            <option value="">Select Type</option>
                                            <option value="housing" ${allowance.allowance_type === 'housing' ? 'selected' : ''}>Housing</option>
                                            <option value="transport" ${allowance.allowance_type === 'transport' ? 'selected' : ''}>Transport</option>
                                            <option value="medical" ${allowance.allowance_type === 'medical' ? 'selected' : ''}>Medical</option>
                                            <option value="phone" ${allowance.allowance_type === 'phone' ? 'selected' : ''}>Phone</option>
                                            <option value="other" ${allowance.allowance_type === 'other' ? 'selected' : ''}>Other</option>
                                        </select>
                                    </div>
                                    <div id="edit_other_allowance_container" class="form-group mb-3" style="width: 100%; padding: 15px; display: ${allowance.allowance_type === 'other' ? 'block' : 'none'}">
                                        <label class="form-label">Other Allowance Title</label>
                                        <input
                                            type="text"
                                            id="edit_other_allowance_title"
                                            class="form-control"
                                            placeholder="Enter allowance title"
                                            value="${allowance.title || ''}"
                                        />
                                    </div>
                                    <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                        <label class="form-label">Amount</label>
                                        <input
                                            type="number"
                                            id="edit_amount"
                                            class="form-control"
                                            value="${allowance.amount || 0}"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                `,
                                focusConfirm: false,
                                showCancelButton: true,
                                confirmButtonText: 'Update',
                                cancelButtonText: 'Cancel',
                                didOpen: () => {
                                    // Add the handler function to the window object
                                    window.handleEditAllowanceTypeChange = () => {
                                        const allowanceType = document.getElementById('edit_allowance_type').value;
                                        const otherContainer = document.getElementById('edit_other_allowance_container');
                                        otherContainer.style.display = allowanceType === 'other' ? 'block' : 'none';
                                        
                                        // Clear the other title field if not 'other'
                                        if (allowanceType !== 'other') {
                                            document.getElementById('edit_other_allowance_title').value = '';
                                        }
                                    };
                                },
                                preConfirm: () => {
                                    const allowance_type = document.getElementById('edit_allowance_type').value;
                                    const amount = document.getElementById('edit_amount').value;
                                    const title = document.getElementById('edit_other_allowance_title').value;

                                    if (!allowance_type || !amount) {
                                        Swal.showValidationMessage('Please fill in all required fields');
                                        return false;
                                    }

                                    if (allowance_type === 'other' && !title.trim()) {
                                        Swal.showValidationMessage('Please enter a title for the other allowance type');
                                        return false;
                                    }

                                    return { 
                                        allowance_type, 
                                        amount: parseFloat(amount),
                                        title: allowance_type === 'other' ? title.trim() : null
                                    };
                                }
                            });

                            if (updatedData) {
                                const success = await handleAllowanceUpdate(allowanceId, updatedData);
                                if (success) {
                                    Swal.close();
                                    showAllowancesModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error editing allowance:', error);
                            await showErrorMessage(error);
                        }
                    };

                    window.handleDeleteAllowance = async (allowanceId) => {
                        try {
                            const result = await showDeleteConfirmation();
                            if (result.isConfirmed) {
                                const success = await handleAllowanceDelete(allowanceId);
                                if (success) {
                                    Swal.close();
                                    showAllowancesModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error deleting allowance:', error);
                            await showErrorMessage(error);
                        }
                    };
                }
            });

            if (formValues) {
                const { value: newAllowanceData } = await Swal.fire({
                    title: 'Add New Allowance',
                    html: `
                        <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                            <label class="form-label">Allowance Type</label>
                            <select id="new_allowance_type" class="form-control" onchange="handleAllowanceTypeChange()">
                                <option value="">Select Type</option>
                                <option value="housing">Housing</option>
                                <option value="transport">Transport</option>
                                <option value="medical">Medical</option>
                                <option value="phone">Phone</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div id="other_allowance_container" class="form-group mb-3" style="width: 100%; padding: 15px; display: none;">
                            <label class="form-label">Other Allowance Title</label>
                            <input
                                type="text"
                                id="other_allowance_title"
                                class="form-control"
                                placeholder="Enter allowance title"
                            />
                        </div>
                        <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                            <label class="form-label">Amount</label>
                            <input
                                type="number"
                                id="new_amount"
                                class="form-control"
                                min="0"
                                step="0.01"
                            />
                        </div>
                    `,
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: 'Add',
                    cancelButtonText: 'Cancel',
                    didOpen: () => {
                        // Add the handler function to the window object
                        window.handleAllowanceTypeChange = () => {
                            const allowanceType = document.getElementById('new_allowance_type').value;
                            const otherContainer = document.getElementById('other_allowance_container');
                            otherContainer.style.display = allowanceType === 'other' ? 'block' : 'none';
                            
                            // Clear the other title field if not 'other'
                            if (allowanceType !== 'other') {
                                document.getElementById('other_allowance_title').value = '';
                            }
                        };
                    },
                    preConfirm: () => {
                        const allowance_type = document.getElementById('new_allowance_type').value;
                        const amount = document.getElementById('new_amount').value;
                        const title = document.getElementById('other_allowance_title').value;

                        if (!allowance_type || !amount) {
                            Swal.showValidationMessage('Please fill in all required fields');
                            return false;
                        }

                        if (allowance_type === 'other' && !title.trim()) {
                            Swal.showValidationMessage('Please enter a title for the other allowance type');
                            return false;
                        }

                        return { 
                            allowance_type, 
                            amount: parseFloat(amount),
                            title: allowance_type === 'other' ? title.trim() : null
                        };
                    }
                });

                if (newAllowanceData) {
                    try {
                        const success = await handleAllowanceAdd(newAllowanceData);
                        if (success) {
                            Swal.close();
                            showAllowancesModal();
                        }
                    } catch (error) {
                        console.error('Error adding allowance:', error);
                        await showErrorMessage(error);
                    }
                }
            }
        } catch (error) {
            console.error('Error managing allowances:', error);
            await showErrorMessage(error);
        }
    };

    const showContractsModal = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/contracts/${employeeId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            window.currentContracts = response.data;

            const formatDate = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString('en-GB'); // This will format as dd/mm/yyyy
            };

            const formatContractType = (type) => {
                if (!type) return '';
                return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
            };

            const tableHtml = `
                <div class="table-responsive" style="max-height: 400px; overflow-y: auto; padding: 10px; border-radius: 10px;">
                    <table class="table table-bordered table-hover">
                        <thead class="bg-light">
                            <tr>
                                <th>Contract Type</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Salary</th>
                                <th class="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${response.data.map((contract) => `
                                <tr>
                                    <td>${formatContractType(contract.contract_type)}</td>
                                    <td>${formatDate(contract.start_date)}</td>
                                    <td>${formatDate(contract.end_date)}</td>
                                    <td>${contract.salary || ''}</td>
                                    <td class="text-center d-flex justify-content-center gap-2">
                                        <button 
                                            type="button"
                                            class="btn btn-primary btn-sm"
                                            onclick="window.handleEditContract(${contract.id})"
                                            title="Edit Contract">
                                            Edit
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-danger btn-sm"
                                            onclick="window.handleDeleteContract(${contract.id})"
                                            title="Delete Contract">
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            `;

            const { value: formValues } = await Swal.fire({
                title: 'Manage Contracts',
                html: tableHtml,
                showCancelButton: true,
                showConfirmButton: true,
                confirmButtonText: 'Add New Contract',
                cancelButtonText: 'Close',
                width: '900px',
                didOpen: () => {
                    window.handleEditContract = async (contractId) => {
                        try {
                            const contract = window.currentContracts.find(c => c.id === contractId);
                            
                            if (!contract) {
                                throw new Error('Contract not found');
                            }

                            const { value: updatedData } = await Swal.fire({
                                title: 'Edit Contract',
                                html: `
                                    <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                        <label class="form-label">Contract Type</label>
                                        <select id="edit_contract_type" class="form-control">
                                            <option value="">Select contract type</option>
                                            <option value="open" ${contract.contract_type === 'open' ? 'selected' : ''}>Open</option>
                                            <option value="fixed_term" ${contract.contract_type === 'fixed_term' ? 'selected' : ''}>Fixed Term</option>
                                        </select>
                                    </div>
                                    <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                        <label class="form-label">Start Date (MM/DD/YYYY)</label>
                                        <input
                                            type="date"
                                            id="edit_start_date"
                                            class="form-control"
                                            value="${formatDateForInput(contract.start_date)}"
                                        />
                                    </div>
                                    <div class="form-group mb-3" id="end_date_container" style="display: ${contract.contract_type === 'fixed_term' ? 'block' : 'none'}; width: 100%; padding: 15px;">
                                        <label class="form-label">End Date (MM/DD/YYYY)</label>
                                        <input
                                            type="date"
                                            id="edit_end_date"
                                            class="form-control"
                                            value="${formatDateForInput(contract.end_date)}"
                                        />
                                    </div>
                                    <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                        <label class="form-label">Salary</label>
                                        <input
                                            type="number"
                                            id="edit_salary"
                                            class="form-control"
                                            min="0"
                                            step="0.01"
                                            value="${contract.salary || ''}"
                                        />
                                    </div>
                                `,
                                focusConfirm: false,
                                showCancelButton: true,
                                confirmButtonText: 'Update',
                                cancelButtonText: 'Cancel',
                                didOpen: () => {
                                    const contractTypeSelect = document.getElementById('edit_contract_type');
                                    const handleContractTypeChange = () => {
                                        const contractType = contractTypeSelect.value;
                                        const endDateContainer = document.getElementById('end_date_container');
                                        endDateContainer.style.display = contractType === 'fixed_term' ? 'block' : 'none';
                                        
                                        if (contractType === 'open') {
                                            document.getElementById('edit_end_date').value = '';
                                        }
                                    };
                                    
                                    contractTypeSelect.addEventListener('change', handleContractTypeChange);
                                },
                                preConfirm: () => {
                                    const contract_type = document.getElementById('edit_contract_type').value;
                                    const start_date = parseDateFromInput(document.getElementById('edit_start_date').value);
                                    const end_date = parseDateFromInput(document.getElementById('edit_end_date').value);
                                    const salary = document.getElementById('edit_salary').value;

                                    if (!contract_type || !start_date || !salary) {
                                        Swal.showValidationMessage('Please fill in all required fields');
                                        return false;
                                    }

                                    if (contract_type === 'fixed_term' && !end_date) {
                                        Swal.showValidationMessage('End date is required for fixed-term contracts');
                                        return false;
                                    }

                                    // Validate date format
                                    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                                    // if (!dateRegex.test(document.getElementById('edit_start_date').value)) {
                                    //     Swal.showValidationMessage('Start date must be in DD/MM/YYYY format');
                                    //     return false;
                                    // }

                                    // if (contract_type === 'fixed_term' && !dateRegex.test(document.getElementById('edit_end_date').value)) {
                                    //     Swal.showValidationMessage('End date must be in DD/MM/YYYY format');
                                    //     return false;
                                    // }

                                    return { 
                                        contract_type, 
                                        start_date, 
                                        end_date: contract_type === 'fixed_term' ? end_date : null,
                                        salary: parseFloat(salary)
                                    };
                                }
                            });

                            if (updatedData) {
                                const success = await handleContractUpdate(contractId, updatedData);
                                if (success) {
                                    Swal.close();
                                    showContractsModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error editing contract:', error);
                            await showErrorMessage(error);
                        }
                    };

                    window.handleDeleteContract = async (contractId) => {
                        try {
                            const result = await showDeleteConfirmation();
                            if (result.isConfirmed) {
                                const success = await handleContractDelete(contractId);
                                if (success) {
                                    Swal.close();
                                    showContractsModal();
                                }
                            }
                        } catch (error) {
                            console.error('Error deleting contract:', error);
                            await showErrorMessage(error);
                        }
                    };
                }
            });

            if (formValues) {
                const { value: newContractData } = await Swal.fire({
                    title: 'Add New Contract',
                    html: `
                        <div style = {{padding: '10px' }}>  

                            <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                <label class="form-label">Contract Type</label>
                                <select id="new_contract_type" class="form-control">
                                    <option value="">Select contract type</option>
                                    <option value="open">Open</option>
                                    <option value="fixed_term">Fixed Term</option>
                                </select>
                            </div>
                            <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                <label class="form-label">Start Date (MM/DD/YYYY)</label>
                                <input
                                    type="date"
                                    id="new_start_date"
                                    class="form-control"
                                />
                            </div>
                            <div class="form-group mb-3" id="new_end_date_container" style="display: none; width: 100%; padding: 15px;">
                                <label class="form-label">End Date (MM/DD/YYYY)</label>
                                <input
                                    type="date"
                                    id="new_end_date"
                                    class="form-control"
                                />
                            </div>
                            <div class="form-group mb-3" style="width: 100%; padding: 15px;">
                                <label class="form-label">Salary</label>
                                <input
                                    type="number"
                                    id="new_salary"
                                    class="form-control"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>
                    `,
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: 'Add',
                    cancelButtonText: 'Cancel',
                    didOpen: () => {
                        const contractTypeSelect = document.getElementById('new_contract_type');
                        const handleContractTypeChange = () => {
                            const contractType = contractTypeSelect.value;
                            const endDateContainer = document.getElementById('new_end_date_container');
                            endDateContainer.style.display = contractType === 'fixed_term' ? 'block' : 'none';
                            
                            if (contractType === 'open') {
                                document.getElementById('new_end_date').value = '';
                            }
                        };
                        
                        contractTypeSelect.addEventListener('change', handleContractTypeChange);
                    },
                    preConfirm: () => {
                        const contract_type = document.getElementById('new_contract_type').value;
                        const start_date = formatDateForInput(document.getElementById('new_start_date').value);
                        const end_date = formatDateForInput(document.getElementById('new_end_date').value);
                        const salary = document.getElementById('new_salary').value;

                        if (!contract_type || !start_date || !salary) {
                            Swal.showValidationMessage('Please fill in all required fields');
                            return false;
                        }

                        if (contract_type === 'fixed_term' && !end_date) {
                            Swal.showValidationMessage('End date is required for fixed-term contracts');
                            return false;
                        }

                        // Validate date format
                        // const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                        // if (!dateRegex.test(document.getElementById('new_start_date').value)) {
                        //     Swal.showValidationMessage('Start date must be in DD/MM/YYYY format');
                        //     return false;
                        // }

                        // if (contract_type === 'fixed_term' && !dateRegex.test(document.getElementById('new_end_date').value)) {
                        //     Swal.showValidationMessage('End date must be in DD/MM/YYYY format');
                        //     return false;
                        // }

                        return { 
                            contract_type, 
                            start_date, 
                            end_date: contract_type === 'fixed_term' ? end_date : null,
                            salary: parseFloat(salary)
                        };
                    }
                });

                if (newContractData) {
                    try {
                        const token = localStorage.getItem('token');
                        await axios.post(`${baseUrl}/api/contracts/${employeeId}/`, newContractData, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });
                        await showSuccessMessage('Contract added successfully');
                        Swal.close();
                        showContractsModal();
                    } catch (error) {
                        console.error('Error adding contract:', error);
                        await showErrorMessage(error);
                    }
                }
            }
        } catch (error) {
            console.error('Error managing contracts:', error);
            await showErrorMessage(error);
        }
    };

    // Success message helper
    const showSuccessMessage = async (message) => {
        await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: message,
            confirmButtonColor: '#198754'
        });
    };

    // Error message helper
    const showErrorMessage = async (error) => {
        await Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message || 'An error occurred',
            confirmButtonColor: '#dc3545'
        });
    };

    // Generic delete confirmation
    const showDeleteConfirmation = async () => {
        return await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });
    };

    const handleQualificationUpdate = async (qualificationId, updatedData) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`${baseUrl}/api/qualifications/${employeeId}/${qualificationId}/`, updatedData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            await showSuccessMessage('Qualification updated successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleQualificationDelete = async (qualificationId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${baseUrl}/api/qualifications/${employeeId}/${qualificationId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            await showSuccessMessage('Qualification deleted successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleQualificationAdd = async (qualData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${baseUrl}/api/qualifications/${employeeId}/`, qualData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            setModalData(prev => ({
                ...prev,
                qualifications: [...prev.qualifications, response.data]
            }));
            
            await showSuccessMessage('Qualification added successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleAllowanceUpdate = async (allowanceId, updatedData) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`${baseUrl}/api/allowances/${employeeId}/${allowanceId}/`, updatedData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            await showSuccessMessage('Allowance updated successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleAllowanceDelete = async (allowanceId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${baseUrl}/api/allowances/${employeeId}/${allowanceId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            await showSuccessMessage('Allowance deleted successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleAllowanceAdd = async (allowanceData) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${baseUrl}/api/allowances/${employeeId}/`, allowanceData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            setModalData(prev => ({
                ...prev,
                allowances: [...prev.allowances, response.data]
            }));
            
            await showSuccessMessage('Allowance added successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleDocumentUpdate = async (documentId, updatedData) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`${baseUrl}/api/documents/${employeeId}/${documentId}/`, updatedData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            await showSuccessMessage('Document updated successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleDocumentDelete = async (documentId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${baseUrl}/api/documents/${employeeId}/${documentId}/`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            await showSuccessMessage('Document deleted successfully');
            return true;
        } catch (error) {
            await showErrorMessage(error);
            return false;
        }
    };

    const handleContractUpdate = async (contractId, updatedData) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`${baseUrl}/api/contracts/${employeeId}/${contractId}/`, updatedData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Contract updated successfully'
            });
            return true;
        } catch (error) {
            console.error('Error updating contract:', error);
            await showErrorMessage(error);
            return false;
        }
    };

    const handleContractDelete = async (contractId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${baseUrl}/api/contracts/${employeeId}/${contractId}/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Contract deleted successfully'
            });
            return true;
        } catch (error) {
            console.error('Error deleting contract:', error);
            await showErrorMessage(error);
            return false;
        }
    };

    // Helper function to format date for input value
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to parse date from YYYY-MM-DD format
    const parseDateFromInput = (dateString) => {
        if (!dateString) return '';
        return dateString; // No need to parse since it's already in YYYY-MM-DD format
    };

    // Add these state variables after other useState declarations
    const [showDisciplinaryModal, setShowDisciplinaryModal] = useState(false);
    const [disciplinaryFormData, setDisciplinaryFormData] = useState({
        record_type: '',
        record_file: null,
        employee: ''
    });
    const [disciplinaryFormLoading, setDisciplinaryFormLoading] = useState(false);
    const [disciplinaryFormError, setDisciplinaryFormError] = useState(null);

    // Add these functions before the return statement
    const handleDisciplinaryButtonClick = () => {
        setDisciplinaryFormData({
            record_type: '',
            record_file: null,
            employee: employeeId
        });
        setShowDisciplinaryModal(true);
    };

    const handleDisciplinaryCloseModal = () => {
        setShowDisciplinaryModal(false);
        setDisciplinaryFormData({
            record_type: '',
            record_file: null,
            employee: ''
        });
        setDisciplinaryFormError(null);
    };

    const handleDisciplinaryInputChange = (e) => {
        const { name, value, files } = e.target;
        setDisciplinaryFormData(prev => ({
            ...prev,
            [name]: files ? files[0] : value
        }));
    };

    const handleDisciplinarySubmit = async (e) => {
        e.preventDefault();
        setDisciplinaryFormLoading(true);
        setDisciplinaryFormError(null);

        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();
            
            // Append all form fields to FormData
            Object.keys(disciplinaryFormData).forEach(key => {
                if (disciplinaryFormData[key] !== null) {
                    formDataToSend.append(key, disciplinaryFormData[key]);
                }
            });

            await axios.post(
                `${baseUrl}/api/disciplinary-records/`,
                formDataToSend,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            await Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Disciplinary record added successfully',
                confirmButtonColor: '#3085d6'
            });

            handleDisciplinaryCloseModal();
        } catch (error) {
            console.error('Error adding disciplinary record:', error);
            setDisciplinaryFormError(error.response?.data?.message || 'Failed to add disciplinary record. Please try again.');
        } finally {
            setDisciplinaryFormLoading(false);
        }
    };

    // Add this JSX right before the closing </div> of the main container
    const renderDisciplinaryModal = () => {
        if (!showDisciplinaryModal) return null;

        return (
            <div className="disciplinary-modal-overlay" onClick={handleDisciplinaryCloseModal}>
                <div className="disciplinary-modal-content" onClick={e => e.stopPropagation()}>
                    <div className="disciplinary-modal-header">
                        <div className="disciplinary-modal-title">
                            <h2>Add Disciplinary</h2>
                            <p className="disciplinary-modal-subtitle">
                                {formData.firstName} {formData.lastName}
                            </p>
                        </div>
                        <button className="disciplinary-modal-close" onClick={handleDisciplinaryCloseModal}>
                            <FaTimes />
                        </button>
                    </div>

                    <form onSubmit={handleDisciplinarySubmit} className="disciplinary-add-form">
                        <div className="disciplinary-form-body">
                            {disciplinaryFormError && (
                                <div className="disciplinary-form-error">
                                    <FaExclamationTriangle />
                                    <p>{disciplinaryFormError}</p>
                                </div>
                            )}

                            <div className="disciplinary-form-group">
                                <label htmlFor="record_type">Record Type</label>
                                <select
                                    id="record_type"
                                    name="record_type"
                                    value={disciplinaryFormData.record_type}
                                    onChange={handleDisciplinaryInputChange}
                                    required
                                    className="disciplinary-form-select"
                                >
                                    <option value="">Select Record Type</option>
                                    <option value="warning">Warning</option>
                                    <option value="suspension">Suspension</option>
                                    <option value="termination">Termination</option>
                                    <option value="notice">Notice</option>
                                </select>
                            </div>

                            <div className="disciplinary-form-group">
                                <label htmlFor="record_file" className="disciplinary-file-label">
                                    <FaUpload />
                                    <span>Upload Document</span>
                                </label>
                                <input
                                    type="file"
                                    id="record_file"
                                    name="record_file"
                                    onChange={handleDisciplinaryInputChange}
                                    className="disciplinary-form-file"
                                    accept=".pdf,.doc,.docx,.txt"
                                    required
                                />
                                {disciplinaryFormData.record_file && (
                                    <p className="disciplinary-file-name">
                                        Selected: {disciplinaryFormData.record_file.name}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="disciplinary-form-actions">
                            <button
                                type="button"
                                className="disciplinary-form-cancel"
                                onClick={handleDisciplinaryCloseModal}
                                disabled={disciplinaryFormLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="disciplinary-form-submit"
                                disabled={disciplinaryFormLoading}
                            >
                                {disciplinaryFormLoading ? (
                                    <>
                                        <FaSpinner className="disciplinary-form-spinner" />
                                        Adding Record...
                                    </>
                                ) : (
                                    'Add Record'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="update-employee-container">
                    <div className="page-header">
                        <h1 style={{color: '#fff'}}>Update Employee</h1>
                        <p style={{color: '#fff'}}>Update employee information and details</p>
                    </div>

                    <form onSubmit={handleSubmit} className="update-form">
                        {/* Personal Information Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <h3>Personal Information</h3>
                            </div>
                            <div className="form-grid">
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label>Employee Photo</label>
                                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                        {formData.photo && (
                                            <img 
                                                src={typeof formData.photo === 'string' ? formData.photo : URL.createObjectURL(formData.photo)} 
                                                alt="Employee" 
                                                style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%' }}
                                            />
                                        )}
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        // Check file size (5MB = 5 * 1024 * 1024 bytes)
                                                        if (file.size > 5 * 1024 * 1024) {
                                                            Swal.fire({
                                                                icon: 'error',
                                                                title: 'File Too Large',
                                                                text: 'Please select an image smaller than 5MB',
                                                                confirmButtonColor: '#1e3a8a'
                                                            });
                                                            // Clear the input
                                                            e.target.value = '';
                                                            return;
                                                        }
                                                        setFormData(prev => ({ ...prev, photo: file }));
                                                    } else {
                                                        // If no file is selected, keep the existing photo
                                                        setFormData(prev => ({ ...prev, photo: prev.photo }));
                                                    }
                                                }}
                                                style={{ width: '100%' }}
                                            />
                                            <small style={{ color: '#64748b', fontSize: '0.875rem' }}>
                                                Maximum file size: 5MB. Supported formats: JPG, PNG, GIF
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>
                                        First Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        className={errors.firstName ? 'error' : ''}
                                    />
                                    {errors.firstName && <div className="error-message">{errors.firstName}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Last Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        className={errors.lastName ? 'error' : ''}
                                    />
                                    {errors.lastName && <div className="error-message">{errors.lastName}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Phone Number <span className="required">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleInputChange}
                                        className={errors.phoneNumber ? 'error' : ''}
                                    />
                                    {errors.phoneNumber && <div className="error-message">{errors.phoneNumber}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Date of Birth <span className="required">*</span>
                                    </label>
                                    <input 
                                        type="date" 
                                        name="date_of_birth" 
                                        value={formData.date_of_birth ? dayjs(formData.date_of_birth).format('YYYY-MM-DD') : ''} 
                                        onChange={(e) => {
                                            const date = dayjs(e.target.value).format('YYYY-MM-DD');
                                            handleInputChange({
                                                target: {
                                                    name: 'date_of_birth',
                                                    value: date
                                                }
                                            });
                                        }} 
                                        className={errors.date_of_birth ? 'error' : ''} 
                                    />
                                    {errors.date_of_birth && <div className="error-message">{errors.date_of_birth}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Gender <span className="required">*</span>
                                    </label>
                                    <select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                        className={errors.gender ? 'error' : ''}
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                    </select>
                                    {errors.gender && <div className="error-message">{errors.gender}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Marital Status <span className="required">*</span>
                                    </label>
                                    <select
                                        name="marital_status"
                                        value={formData.marital_status}
                                        onChange={handleInputChange}
                                        className={errors.marital_status ? 'error' : ''}
                                    >
                                        <option value="">Select Status</option>
                                        <option value="single">Single</option>
                                        <option value="married">Married</option>
                                       
                                    </select>
                                    {errors.marital_status && <div className="error-message">{errors.marital_status}</div>}
                                </div>

                                <div className="form-group">
                                    <label>Home Address</label>
                                    <input
                                        type="text"
                                        name="home_address"
                                        value={formData.home_address}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Working Days Per Month</label>
                                    <select
                                        name="workingDaysPerMonth"
                                        value={formData.workingDaysPerMonth}
                                        onChange={handleInputChange}
                                    >
                                        <option value="22">22</option>
                                        <option value="26">26</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Work Information Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <h3>Work Information</h3>
                            </div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>
                                        Position <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="position"
                                        value={formData.position}
                                        onChange={handleInputChange}
                                        className={errors.position ? 'error' : ''}
                                    />
                                    {errors.position && <div className="error-message">{errors.position}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Department <span className="required">*</span>
                                    </label>
                                    <Select
                                        value={selectedDepartment}
                                        onChange={(option) => {
                                            setSelectedDepartment(option);
                                            handleInputChange({
                                                target: {
                                                    name: 'department',
                                                    value: option?.value || ''
                                                }
                                            });
                                        }}
                                        options={departments.map(dept => ({
                                            value: dept.id,
                                            label: dept.name
                                        }))}
                                        className={errors.department ? 'react-select-container error' : 'react-select-container'}
                                        classNamePrefix="react-select"
                                    />
                                    {errors.department && <div className="error-message">{errors.department}</div>}
                                </div>
                            </div>
                        </div>


                        {/* Next of Kin Section */}
                        <div className="form-section">
                            <div className="section-header">
                                <h3>Next of Kin Details</h3>
                            </div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>
                                        
                                        Next of Kin Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="next_of_kin_name"
                                        value={formData.next_of_kin_name}
                                        onChange={handleInputChange}
                                        className={errors.next_of_kin_name ? 'error' : ''}
                                    />
                                    {errors.next_of_kin_name && <div className="error-message">{errors.next_of_kin_name}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Next of Kin Contact <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="next_of_kin_contact"
                                        value={formData.next_of_kin_contact}
                                        onChange={handleInputChange}
                                        className={errors.next_of_kin_contact ? 'error' : ''}
                                    />
                                    {errors.next_of_kin_contact && <div className="error-message">{errors.next_of_kin_contact}</div>}
                                </div>

                                <div className="form-group">
                                    <label>
                                        Next of Kin Address
                                    </label>
                                    <input
                                        type="text"
                                        name="next_of_kin_address"
                                        value={formData.next_of_kin_address}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>
                                        Relationship <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="next_of_kin_relationship"
                                        value={formData.next_of_kin_relationship}
                                        onChange={handleInputChange}
                                        className={errors.next_of_kin_relationship ? 'error' : ''}
                                    />
                                    {errors.next_of_kin_relationship && <div className="error-message">{errors.next_of_kin_relationship}</div>}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '20px'}}    className="row mt-4 mb-4">
                            <div className="col-md-2 mb-2">
                                <button 
                                    type="button"
                                    className="btn btn-primary w-100"
                                    style={{
                                        backgroundColor: '#28a745',
                                        borderColor: '#28a745',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        fontWeight: '500'
                                    }}
                                    onClick={() => handleButtonClick('qualifications')}
                                    disabled={loading}
                                >
                                    
                                    Qualifications
                                </button>
                            </div>
                            <div className="col-md-2 mb-2">
                                <button 
                                    type="button"
                                    className="btn btn-primary w-100"
                                    style={{
                                        backgroundColor: '#dc3545',
                                        borderColor: '#dc3545',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        fontWeight: '500'
                                    }}
                                    onClick={() => handleButtonClick('documents')}
                                    disabled={loading}
                                >
                                    
                                    Documents
                                </button>
                            </div>

                            <div className="col-md-2 mb-2">
                                <button 
                                    type="button"
                                    className="btn btn-primary w-100"
                                    style={{
                                        backgroundColor: '#fd7e14',
                                        borderColor: '#fd7e14',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        fontWeight: '500'
                                    }}
                                    onClick={handleDisciplinaryButtonClick}
                                    disabled={loading}
                                >
                                    Disciplinary
                                </button>
                            </div>
                            <div className="col-md-2 mb-2">
                                <button 
                                    type="button"
                                    className="btn btn-primary w-100"
                                    style={{
                                        backgroundColor: '#fd7e14',
                                        borderColor: '#fd7e14',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        fontWeight: '500'
                                    }}
                                    onClick={() => handleButtonClick('allowances')}
                                    disabled={loading}
                                >
                                    
                                    Allowances
                                </button>
                            </div>
                            <div className="col-md-2 mb-2">
                                <button 
                                    type="button"
                                    className="btn btn-primary w-100"
                                    style={{
                                        backgroundColor: '#6610f2',
                                        borderColor: '#6610f2',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        fontWeight: '500'
                                    }}
                                    onClick={() => handleButtonClick('contracts')}
                                    disabled={loading}
                                >
                                    
                                    Contracts
                                </button>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                onClick={() => navigate('/admin-manager/employees')}
                                className="cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={submitting}
                            >
                                {submitting ? 'Updating...' : 'Update Employee'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {renderDisciplinaryModal()}
        </>
    );
};

export default UpdateEmployee;